import { Permissions } from '@billda/permissions-ui';
import {
  UserStatus,
  type OrganisationRoleDto,
  type UserRoleDto,
} from '../api/userManagementApi';

// Mirrors backend RoleConstants.Admin — assigning or inviting this role without
// users.manage_admins is rejected, so it must not be offered as an option.
export const ADMIN_ROLE_NAME = 'Admin';

export interface RoleChangeContext {
  activeRole: UserRoleDto | undefined;
  status: UserStatus;
  isCurrentUser: boolean;
  canManageAdmins: boolean;
}

export function canEditUserRole({
  activeRole,
  status,
  isCurrentUser,
  canManageAdmins,
}: RoleChangeContext): boolean {
  if (activeRole == null || isCurrentUser) {
    return false;
  }

  if (status === UserStatus.Offboarding || status === UserStatus.Inactive) {
    return false;
  }

  const targetHasManage = activeRole.permissions?.includes(
    Permissions.Users.Manage
  );

  return !targetHasManage || canManageAdmins;
}

// Mirrors StartOffboardMutation/CancelOffboardMutation/CompleteOffboardMutation:
// self-offboarding is rejected, and offboarding a target whose role has
// users.manage requires users.manage_admins.
export function canManageUserOffboarding({
  roles,
  isCurrentUser,
  canManageAdmins,
}: {
  roles: UserRoleDto[];
  isCurrentUser: boolean;
  canManageAdmins: boolean;
}): boolean {
  if (isCurrentUser) {
    return false;
  }

  const targetHasManage = roles.some(role =>
    role.permissions?.includes(Permissions.Users.Manage)
  );

  return !targetHasManage || canManageAdmins;
}

export function getAssignableRoles(
  roles: OrganisationRoleDto[],
  canManageAdmins: boolean
): OrganisationRoleDto[] {
  if (canManageAdmins) {
    return roles;
  }

  return roles.filter(role => role.name !== ADMIN_ROLE_NAME);
}
